//---- components ---------
//---- mixins -------------
import globalMixin from '../common/global-mixin.js';
//---- services -----------
import AuthService from './auth.service.js'
import BrowserTabService from './browser-tab.service.js'
import kmOfflineService from './kmoffline.service.js';
import Notifications from './notification.js'
//---- other --------------
import logger from './app-logger.js'
import routes from '../routes.js';
import store from '@/app/store/index.js'
//---- 3rd-parties --------
import axios from 'axios';
import VueApp from '@/main.js'

const debug = false;

// See action documentation at https://github.com/axios/axios

// /**
//  * Creating the instance of Axios
//  * It is because, in large scale application we may need to consume APIs from more than single server,
//  * So, may need to create multiple http client with different config
//  * Only this client will be used rather than axios in the application
//  **/
// const httpClient = axios.create(config);

const httpClient = axios;

// =================== AJAX SERVICE (API) ==================================

class AjaxService {

  // do not use that, instead, just do a real button like in KMOfflineDownload.vue
  // here's an example:
  //   <b-button
  //      size="sm"
  //      :href="kmOfflineDownloadUrl"
  //      download
  //      variant="success"
  //   >{{$t('label.disconnectedUpdate.download')}}</b-button>

  // downloadBinaryFile(url) {
  // }

  get(url, extra) {
    return httpClient.get(url, extra);
  }

  getWithParams(url, params, extraConfig) {
    var config = { ...extraConfig };
    config.params = params;
    return httpClient.get(url, config)
  }

  postWithParams(url, params, extraConfig) {
    var defaultPostParameters = {
      method: 'post',
      url: url,
      params: params,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8' },
    }

    var postParameters;
    if (extraConfig)
      postParameters = { ...defaultPostParameters, ...extraConfig }  // overwrite default with extra
    else
      postParameters = defaultPostParameters

    return httpClient(postParameters);
  }

  post(url, data, extraParameters) {
    return httpClient.post(url, data, {...extraParameters, maxRedirects:0 });
  }
  // to get the binary file with the help of post and request data
  getBinary(url, data) {
    return httpClient.post(url,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/octet-stream'
        },
        responseType: "blob"
      })
  }

  // for backward compatibility
  postData(url, data, extraParameters) {
    return this.postFormData(url, data, extraParameters)
  }

  postFormData(url, data, extraParameters) {

    if (!(data instanceof FormData))
      data = this.getFormData(data);

    var defaultPostParameters = {
      method: 'post',
      url: url,
      data: data,
      headers: { 'Content-Type': 'multipart/form-data; charset=utf-8' },
    }

    var postParameters;
    if (extraParameters)
      postParameters = { ...defaultPostParameters, ...extraParameters }  // overwrite default with extra
    else
      postParameters = defaultPostParameters

    return httpClient(postParameters);
  }

  postUrlEncodedData(url, data, extraParameters) {
    var defaultPostParameters = {
      method: 'post',
      url: url,
      data: data,
      transformRequest: [convertToUrlEncodedString],
      headers: { 'Content-Type': "application/x-www-form-urlencoded; charset=utf-8" }
    }

    var postParameters;
    if (extraParameters)
      postParameters = { ...defaultPostParameters, ...extraParameters }  // overwrite default with extra
    else
      postParameters = defaultPostParameters

    return httpClient(postParameters);
  }

  //-------------------------------------------------------------------------

  // convert a regular js object into a FormData
  getFormData(object) {
    const formData = new FormData();
    if (object)
      Object.keys(object).forEach(key => formData.append(key, object[key]));
    return formData;
  }

}

// =================== REQUESTS INTERCEPTOR ==================================

// /**
//  * Auth interceptors
//  * @description Configuration related to AUTH token can be done in interceptors.
//  * Currenlty it is just doing nothing but idea to to show the capability of axios and its interceptors
//  * In future, interceptors can be created into separate files and consumed into multiple http clients
//  * @param {*} config
//  */
// const authInterceptor = config => {
//   /** add auth token */
//   return config;
// };

// const loggerInterceptor = config => {
//   /** Add logging here */
//   return config;
// };

// /** Adding the request interceptors */
// httpClient.interceptors.request.use(authInterceptor);
// httpClient.interceptors.request.use(loggerInterceptor);

const makeAbsoluteUrlInterceptor = config => {
  // add the backend url prefix 
  // ex: /ping.action -> http://localhost:8080/advisor/ping.action
  if (config?.url && config.url.startsWith("/"))
    config.url = store.getters["backendUrl"] + config.url
  return config;
}

// Prevent non-active tabs from posting
const nonActiveTabGuard = config => {
  //     can't make non-get api calls if we're not the active tab
  //     because we can't change the state of the app
  //     store.getters('header/initIsActiveTab') // to update the value
  if (!BrowserTabService.isActiveTab() && !BrowserTabService.areActionsAllowed() && config.method !== 'get') {
    const controller = new AbortController();
    controller.abort();
  }

  return config;
}

const csrfTokenInterceptor = request => {
  // console.log("csrf token: " + document.querySelector("meta[name='csrf-token']").getAttribute("content"))
  request.headers['csrfToken'] = document.querySelector("meta[name='csrf-token']").getAttribute("content")
  return request;
}


httpClient.interceptors.request.use(makeAbsoluteUrlInterceptor);
httpClient.interceptors.request.use(nonActiveTabGuard);
httpClient.interceptors.request.use(csrfTokenInterceptor, error => console.error(error));

httpClient.maxRedirects = 0;
httpClient.defaults.maxRedirects = 0;
httpClient.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
httpClient.defaults.headers.common['request-type'] = 'ajax' // for all requests

// ===================  RESPONSE INTERCEPTOR ==================================

httpClient.interceptors.response.use(
  response => { return handleHttpSuccess(response) },
  error => { return handleHttpError(error) }
);

const invalidJsonResponse = (response) => {

  const url = response.request.responseURL;
  let data = response.data;

  if (data.length > 1000)
    data = data.substring(0, 1000) + "...";

  Notifications.htmlError(VueApp.$t('invalidJsonResponseMessage') + '\n' + url + '\n' + data);
}

const extendSessionTimeout = (url) => {
  // we don't want to extend session for hServiceConnectivity
  if (url.includes("hServiceConnectivity.jsp"))
    return;
  AuthService.extendSessionTimeout();
}

const handleHttpSuccess = (response) => {

  if (debug)
    console.debug("handling response from: " + response?.config?.url)
    
  if (!response) {
    console.debug("No response in handleHttpSuccess!")
    store.dispatch("hideGlobalLoadingOverlay", "error response from server needs to be displayed");
    Notifications.htmlWarn(VueApp.$t('error.no-connectivity.application'), 'app-unavailable');
    return Promise.reject({ response });  // is that the way to go??
  }

  Notifications.removeNotification('app-unavailable');
  Notifications.removeNotification('backend-unreachable');
  if (store.getters["kmOffline/isKMOffline"]) {
    // set that solo backend is running
    store.dispatch("kmOffline/setSoloBackendRunninStatus", true);
  }

  if (response.data && isJson(response))
    return handleJsonResponse(response)

  // response doesn't have data, or response is not json

  // if response is an octect stream, we're downloading? 
  if ((response?.config?.headers?.Accept || "").includes('application/octet-stream')) {
    extendSessionTimeout(response?.config?.url)
    return Promise.resolve(response);
  }

  if (response.data && response.data.includes("<pre>Cannot POST")) {
    return Promise.reject({ response, error: { message: response.data } });
  }

  invalidJsonResponse(response);
  return Promise.reject({ response, error: { message: response.data } });
};

const handleJsonResponse = (response) => {

  // consume the csrfToken if passed
  if (response?.data?.session?.csrfToken) {
    document.querySelector("meta[name='csrf-token']").setAttribute("content", response.data.session.csrfToken);
  }

  if (response.data.notifications) {
    Notifications.notificationFilter(response.data.notifications);
    delete response.data.notifications;
  }

  // global interceptor for connectivityStatus
  if (response.data.connectivityStatus) {
    kmOfflineService.updateVuexConnectivityInfo(response.data.connectivityStatus);
  }

  // global recoverable error management
  // exceptions should not endup in error messages
  // error messages are for failures, see next section
  if (response.data.status == "recoverable-global-exception") {
    switch (response.data.key) {
      case 'no-session':
      case 'session-expired': {
          const fromLoggedIn = store.getters['auth/loggedIn'];
          // if we think we are logged in, then it's a timeout or session loss 
          // (server restarted or similar cause). 
          // if we don't think we are login, no need to write any error, so use
          // the onLogout method.
          store.dispatch(fromLoggedIn ? 'onTimeout' : 'onLogout');

          let hash = '';
          if (response.data.kmofflineAuth)
            hash = "#kmOfflineAuth"
          if (response.data.kmofflineRegistration)
            hash = "#kmOfflineRegistration"
          // if the current route is not public, need to go back to login
          if (!routes?.to?.meta?.public)
            globalMixin.methods.safeRouterPush('/login' + hash)
          break;
        }
      case 'no-project-open':
        globalMixin.methods.safeRouterPush("/project-selection");
        extendSessionTimeout(response?.config?.url)
        break;
      case 'advisor-application-error':
        store.dispatch('applicationError', response.data.data[0]).then(
          () => {
            store.dispatch("onLogout");
            store.dispatch("auth/logout")
            globalMixin.methods.safeRouterPush('/advisor-application-error')
          }
        );
        // Notifications.warn(response.data.data[0], 'advisor-application-error');
        break;
      case 'redirect':
        if (response.data.redirect) {
          let redirectUrl = response.data.redirect;
          if (redirectUrl.startsWith("/"))
            globalMixin.methods.safeRouterPush(redirectUrl);
          else
            window.location.href = redirectUrl;
        }
        break;
      default:
        // this condition is to prevent endless loops
        if (!response.request.responseURL.endsWith("clientSideErrorLogger.action"))
          reportErrorToServer({
            message: "exception key not handled: " + response.data.key,
            url: response.config.url
          })
        logger.alertDev('key not handled: ' + response.data.key)
        break;
    }
    return Promise.reject({ response });  // is that the way to go??
  } // end if exception

  else if (response.data.status == "failure") {
    var errorKey = response.data.ErrorKey || response.data.key
    var errorMessage = response.data.localizedMessage || response.data.ErrorMessage
    if (errorMessage != null) {
      // if errorKey and error Message both defined, 
      if (errorKey != null) {
        // show the error notification, in case error message only
        Notifications.htmlError(errorMessage, errorKey);
        store.dispatch("hideGlobalLoadingOverlay", "Global Error Handler");
        // allow programmer to handle
        return Promise.reject({ response })
      }
      else {
        // use the url as failure ID 
        Notifications.removeNotification(response.config.url)
        Notifications.htmlError(errorMessage, response.config.url);
        store.dispatch("hideGlobalLoadingOverlay", "Global Error Handler");
      }
    }

    else if (errorKey != null) {
      switch (errorKey) {
        case "username-not-provided":
        case "Invalid name or password.":
          break;
        case "advisor-application-error":
          store.dispatch('applicationError', response.data.data[0]).then(
            () => globalMixin.methods.safeRouterPush('/advisor-application-error')
          );
          // Notifications.htmlError(response.data.data[0].replaceAll('\n', '<br/>'));
          break;
        default:
          extendSessionTimeout(response?.config?.url)
          // show the error notification based on errorKey
          Notifications.htmlError(VueApp.$t(errorKey), response.config.url); // use the url as failure ID
          store.dispatch("hideGlobalLoadingOverlay", "Global Error Handler");
      }
      return Promise.reject({ response, errorKey });
    }
    else {
      // we have a failure response but we don't know what the failure is about
      // because the server didn't send an ErrorKey, localizedMessage or ErrorMessage

      // we need to display a generic error message
      Notifications.htmlError(VueApp.$t('kaidara.error.unknown'), response.config.url);

      // this condition is to prevent endless loops
      if (!response.request.responseURL.endsWith("clientSideErrorLogger.action"))
        reportErrorToServer({
          message: "failure response didn't have explanation (ErrorKey, localizedMessage or ErrorMessage)",
          url: response.config.url,
          responseData: response.data
        })

      extendSessionTimeout(response?.config?.url)
    }

    return Promise.reject({ response });  // is that the way to go??
  } // end if failure

  // remove error notifications about this URL 
  Notifications.removeNotification(response.config.url);
  // Notifications.clearAllNotification();

  //if status is success and json containing error message
  //just show the popup and load the response data to the screen
  let eMessage = response.data.ErrorMessage
  if (eMessage != null)
    Notifications.htmlError(eMessage);

  if (response?.data?.session?.timeout)
    AuthService.setSessionTimeout(response.data.session.timeout * 60000) // convert minutes to ms
  else
    extendSessionTimeout(response?.config?.url)

  return Promise.resolve(response);
}

const onBackendUnreachable = function() {

  if (store.getters["kmOffline/isKMOffline"]) {
    // set that solo backend is not running
    store.dispatch("kmOffline/setSoloBackendRunninStatus", false);
    store.dispatch("hideGlobalLoadingOverlay", "error response from server needs to be displayed");
    // console.log("Solo backend is not running");

    // if we're logged in, we want to logout
    if (store.getters['auth/loggedIn']) {
      // console.log("No response from server -> logging you out!");
      store.dispatch("onLogout");
      store.dispatch("auth/logout")
      globalMixin.methods.safeRouterPush('/login')
    }
  }
  else {
    store.dispatch('backendUnreachable')
  }
}

const handleHttpError = function (error) {
  if (!error.response) {
    if (error.code == "ECONNABORTED") {
        console.log("Connection aborted. Url: " + error.config.url);
    }
    else if (error.code == "ERR_NETWORK" && store.getters["kmOffline/isKMOffline"]) {

        console.log("No response from server. KMOffline must be down." 
          + "\n\terror.code: " + error.code
          + "\n\turl: " + error.config.url
          );
          onBackendUnreachable();
    }
    else {
        console.error("No response in handleHttpError! " 
                  + "\n\terror.code: " + error.code
                  + "\n\turl: " + error.config.url
                  + "\n\tisOffline: " + store.getters["kmOffline/isKMOffline"]
                  );
        console.error(JSON.stringify(error, null, 2));
        store.dispatch("hideGlobalLoadingOverlay", "error response from server needs to be displayed");
        Notifications.htmlWarn(VueApp.$t('error.no-connectivity.application'), 'app-unavailable');
    }
    return Promise.reject({ errorCode: error.code });  // is that the way to go??
  }

  // the app responded, remove app unavailable notification 
  // (we will add it back in case of a 503)
  Notifications.removeNotification('app-unavailable')

  if (!error.response.status) {
    // this condition is to prevent endless loops
    if (!error.response.request.responseURL.endsWith("clientSideErrorLogger.action"))
      reportErrorToServer({ msg: "Response didn't have any status. Check the console", url: error.config.url })
    console.error("No status for error response: " + JSON.stringify(error.response))
    return;
  }

  switch (error.response.status) {
    case 400: // Bad Request
      // The server cannot or will not process the request due to an apparent 
      // client error (e.g., malformed request syntax, size too large, invalid 
      // request message framing, or deceptive request routing).
      //do something
      Notifications.htmlError("bad request");
      break;

    case 401: // Unauthorized
      // Similar to 403 Forbidden, but specifically for use when authentication
      // is required and has failed or has not yet been provided. The response 
      // must include a WWW-Authenticate header field containing a challenge 
      // applicable to the requested resource.
      // Notifications.htmlWarn("session expired");
      
      // if the response contains a location header, redirect the browser to it
      {
        const headers = error.response.headers;
        // console.log(JSON.stringify(headers))
        const newLocation = headers.location || headers.Location;
        if (newLocation) {
          // redirect the current
          console.trace("redirecting to "+newLocation);
          window.location.href = newLocation;
          return null;
        }
      }
      break;

    case 403: // Forbidden
      // The request contained valid data and was understood by the server, 
      // but the server is refusing action. This may be due to the user not
      // having the necessary permissions for a resource or needing an 
      // account of some sort, or attempting a prohibited action
      // The request should not be repeated.
      routes.replace({
        path: "/login",
        //query: { redirect: router.currentRoute.fullPath }
      });
      break;

    case 404: { // Not Found, Page Not Found, or Server Not Found
      const message = 'This action is not supported.'
      Notifications.htmlError(message);
      store.dispatch("hideGlobalLoadingOverlay", "404 error");
      return Promise.reject({ response: error.response, message: message })
    }

    case 500: // Internal Server Error
      if (error.response.data.includes('Proxy error'))
        onBackendUnreachable()
      else
        Notifications.htmlError(VueApp.$t('internal-error.500'));
      store.dispatch("hideGlobalLoadingOverlay", "500 error");
      return Promise.reject({ response: error.response });

    case 502: // bad gateway
      // why are we redirecting to login? 
      // in such a case JIRA puts the following message, we should not copy it
      // but put something along those lines:
      // Communications Breakdown
      // The JIRA server could not be contacted. This may be a temporary glitch or the server may be down.
      // Close this dialog and press refresh in your browser
      setTimeout(() => {
        routes.replace({
          path: "/login",
        });
      }, 1000);
      break;

    case 503: { // Service Unavailable
      // The server cannot handle the request (because it is overloaded or down for maintenance). 
      // Generally, this is a temporary state.
      const message = VueApp.$t('error.service-unavailable')
      Notifications.htmlError(message, 'app-unavailable');
      store.dispatch("hideGlobalLoadingOverlay", "503 error");
      return Promise.reject({ message: message })
    }

    case 504: { // Gateway Timeout
      // The server was acting as a gateway or proxy and did not receive a timely response from the upstream server.
      const message = VueApp.$t('error.service-too-slow')
      Notifications.htmlError(message, 'app-unavailable');
      store.dispatch("hideGlobalLoadingOverlay", "504 error");
      return Promise.reject({ message: message })
    }

    default: {
      // if we did not have a specific handler for the response code try
      // handling it.
      // convert 1xx -> 100, 2xx -> 200, 3xx -> 300....
      const category = Math.floor(error.response.status / 100) * 100;
      switch (category) {
        case 100: // Informational response
        case 200: // Success
          // both 100 and 200 should be sucess, let it go through
          Notifications.removeNotification('backend-unreachable');
          break;
        case 300: // Redirection -> let it fallthrough for now, but see if we can have global handling later
        case 400: // Client errors
        case 500: { // Server errors
          // both 400 and 500 are errors we don't know how to treat
          // give generic error message
          const message = VueApp.$t('error.generic-http-error') + error.response.status
          Notifications.error(message);
          store.dispatch("hideGlobalLoadingOverlay", error.response.status + " error");
          return Promise.reject({ message: message })
        }
      }
    }
  }
  
  if (error.response && isJson(error.response) && error.response.message) {
    Notifications.htmlError(error.response.message);
    store.dispatch("hideGlobalLoadingOverlay", "Global Error Handler");
    return Promise.reject({ response: error.response });
  }
  else if (error.isAxiosError) {
    if (error.response.status != 401)
      Notifications.htmlError(error.message);
    store.dispatch("hideGlobalLoadingOverlay", "Global Error Handler");
    return Promise.reject({ response: error.response, message: error.message, status: error.response.status })
  }
  else {
    invalidJsonResponse(error.response);
    store.dispatch("hideGlobalLoadingOverlay", "Global Error Handler");
    return Promise.reject({ response: error.response, message: error.response.data });
  }
}

// ======================== UTILITY METHODS =============================================

function reportErrorToServer({ message, url, responseData }) {
  return logger.logClientSideErrorToServer({ msg: message, url, responseData })
}

// convert the data object to a url encoded string
// ex: {key1: value1, key2: value2} -> "key1=value1&key2=value2"
function convertToUrlEncodedString(data = {}) {
  return Object.entries(data)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
}

// check if the response's content-type is json
function isJson(response) {
  var contentType = response && (response.headers['content-type'] || response.headers['Content-type']);

  if (!contentType) {
    
    // this condition is to prevent endless loops
    if (!response.request.responseURL.endsWith("clientSideErrorLogger.action")) {
      console.error("No content type for response of " + response?.config?.url)
      reportErrorToServer("no content-type: this should not happen", response?.config?.url, response?.data);

    }

    return true;
  }

  return contentType.includes('application/json') || contentType.includes('text/json');
}

// ======================== EXPORTS =====================================================

const ajaxService = new AjaxService();
export { httpClient, ajaxService };